import { friendlyError } from '../../../utils/userMessages';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { CalendarMonth as CalendarMonthRounded, CheckCircleRounded, Delete as DeleteOutlineRounded, Event as EventAvailableRounded, Link as LinkRounded, Refresh as SyncRounded } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { listCalendarConnections, connectCalendar, removeCalendar, createIcsFeed } from '../../../services/integrations/calendar';
import LoadingSpinner from '../../common/LoadingSpinner';

const providerMeta = {
  google: { label: 'Google Calendar', color: '#4285F4', text: 'Use Google busy time to protect shared slots and synchronize confirmed TELCOFAIR meetings.' },
  microsoft: { label: 'Microsoft Outlook', color: '#2563EB', text: 'Connect Outlook/Microsoft 365 availability so external busy time prevents double booking.' },
};

export default function CalendarIntegrationSettings() {
  const [info, setInfo] = useState({ available: {}, connections: {} });
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const [icsUrl, setIcsUrl] = useState('');

  const load = async () => {
    setLoading(true);
    try { setInfo(await listCalendarConnections() || { available: {}, connections: {} }); }
    catch (error) { toast.error(friendlyError(error, 'Unable to load calendar connections.')); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (provider) => {
    setBusy(provider);
    try {
      const connected = Boolean(info?.connections?.[provider]?.connected);
      if (connected) {
        await removeCalendar(provider);
        toast.success(`${providerMeta[provider].label} disconnected.`);
      } else {
        const result = await connectCalendar(provider);
        if (result?.ok) toast.success(`${providerMeta[provider].label} connected.`);
      }
      await load();
    } catch (error) { toast.error(friendlyError(error, `Unable to update ${providerMeta[provider].label}.`)); }
    finally { setBusy(''); }
  };

  const createFeed = async () => {
    setBusy('ics');
    try {
      const result = await createIcsFeed();
      setIcsUrl(result?.url || result?.feedUrl || '');
      toast.success('Private calendar feed generated.');
    } catch (error) { toast.error(friendlyError(error, 'Unable to generate calendar feed.')); }
    finally { setBusy(''); }
  };

  return (
    <Stack spacing={1.5} className="tf-calendar-settings-workspace">
      <Paper elevation={0} className="tf-settings-feature-hero" sx={{ '--feature-color': '#4285F4' }}>
        <Box className="tf-settings-feature-watermark"><CalendarMonthRounded /></Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} sx={[{ alignItems: { md: 'center' }, justifyContent: 'space-between' }, { position: 'relative', zIndex: 1 }]}>
          <Stack direction="row" spacing={1.15} sx={{ alignItems: 'center' }}>
            <Box className="tf-settings-feature-icon"><CalendarMonthRounded /></Box>
            <Box>
              <Typography sx={{ fontSize: '.96rem', fontWeight: 950, color: '#172235' }}>Calendar Integration Center</Typography>
              <Typography sx={{ mt: .2, fontSize: '.68rem', color: '#718096', maxWidth: 760 }}>External calendar connections are managed here only. Busy events remove conflicting shared slots; recipients still control when their TELCOFAIR availability is shared.</Typography>
            </Box>
          </Stack>
          <Chip icon={<EventAvailableRounded />} label="Double-booking protection" sx={{ fontWeight: 850, bgcolor: 'rgba(66,133,244,.08)', color: '#2B63B6' }} />
        </Stack>
      </Paper>

      {loading ? <LoadingSpinner compact message="Loading calendar connections…" /> : (
        <Grid container spacing={1.3}>
          {Object.entries(providerMeta).map(([provider, meta]) => {
            const connected = Boolean(info?.connections?.[provider]?.connected);
            const available = Boolean(info?.available?.[provider]);
            const account = info?.connections?.[provider] || {};
            return (
              <Grid key={provider} size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} className="tf-calendar-provider-card" sx={{ '--calendar-color': meta.color }}>
                  <Box className="tf-calendar-provider-art"><CalendarMonthRounded /></Box>
                  <Stack spacing={1.2} sx={{ position: 'relative', zIndex: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box className="tf-calendar-provider-icon"><CalendarMonthRounded /></Box>
                      <Chip size="small" icon={connected ? <CheckCircleRounded /> : undefined} label={connected ? 'CONNECTED' : available ? 'READY' : 'ADMIN SETUP REQUIRED'} sx={{ fontSize: '.58rem', fontWeight: 900 }} />
                    </Stack>
                    <Box>
                      <Typography sx={{ fontSize: '.88rem', fontWeight: 900, color: '#172235' }}>{meta.label}</Typography>
                      <Typography sx={{ mt: .22, fontSize: '.66rem', lineHeight: 1.5, color: '#728096' }}>{meta.text}</Typography>
                    </Box>
                    {connected && <Typography sx={{ fontSize: '.65rem', color: '#0F766E', fontWeight: 750 }}>{account.email || account.name || 'Connected account'}</Typography>}
                    <Button disabled={busy === provider || (!connected && !available)} onClick={() => toggle(provider)} variant={connected ? 'outlined' : 'contained'} color={connected ? 'error' : 'primary'} startIcon={busy === provider ? <CircularProgress size={13} color="inherit" /> : connected ? <DeleteOutlineRounded /> : <LinkRounded />} sx={{ alignSelf: 'flex-start', borderRadius: '10px', fontWeight: 830 }}>
                      {connected ? 'Disconnect' : `Connect ${meta.label}`}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}

          <Grid size={{ xs: 12 }}>
            <Paper elevation={0} className="tf-calendar-feed-card">
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1.1} sx={{ alignItems: 'center' }}>
                  <Box className="tf-calendar-feed-icon"><SyncRounded /></Box>
                  <Box>
                    <Typography sx={{ fontSize: '.82rem', fontWeight: 900, color: '#172235' }}>Apple Calendar / Thunderbird / iCal Feed</Typography>
                    <Typography sx={{ mt: .16, fontSize: '.66rem', color: '#718096' }}>Generate a private subscription URL for confirmed TELCOFAIR meetings. Keep the feed URL private.</Typography>
                  </Box>
                </Stack>
                <Button variant="outlined" disabled={!info?.available?.ics || busy === 'ics'} onClick={createFeed} startIcon={busy === 'ics' ? <CircularProgress size={13} /> : <LinkRounded />} sx={{ borderRadius: '10px', fontWeight: 820 }}>Generate Private Feed</Button>
              </Stack>
              {icsUrl && <TextField fullWidth size="small" value={icsUrl} slotProps={{ input: { readOnly: true } }} sx={{ mt: 1.2 }} helperText="Anyone with this URL can subscribe to your TELCOFAIR meeting feed." />}
            </Paper>
          </Grid>
        </Grid>
      )}
      <Alert severity="info" sx={{ borderRadius: '12px', fontSize: '.7rem' }}>Calendar integrations are intentionally available only from Settings. Meetings and scheduling use these connections automatically for conflict checks without exposing external calendar event details.</Alert>
    </Stack>
  );
}
